import { collectCallDiagnostics } from './phoneDiagnostics';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Web, Inviter } from 'sip.js';
import { CallGETAPI } from './Constants';

export const CONNECT_STATUS = { WAIT_REQUEST_CONNECT: 'idle', CONNECTED: 'connected', DISCONNECTED: 'disconnected' };
export const REGISTER_STATUS = { REGISTERED: 'registered', UNREGISTERED: 'unregistered' };
export const RegisterStatus = REGISTER_STATUS;
export const SessionDirection = { INCOMING: 'incoming', OUTGOING: 'outgoing' };
const Context = createContext(null);
export function useSIPProvider() {
  const context = useContext(Context);
  if (!context) throw new Error('Phone controls must be inside SIPProvider');
  return context;
}

export function SIPProvider({ options, children }) {
  const [sessionManager, setSessionManager] = useState(null);
  const [sessions, setSessions] = useState({});
  const [sessionTimer, setSessionTimer] = useState({});
  const [connectStatus, setConnectStatus] = useState(CONNECT_STATUS.WAIT_REQUEST_CONNECT);
  const [registerStatus, setRegisterStatus] = useState(REGISTER_STATUS.UNREGISTERED);
  const managerRef = useRef(null);
  const pending = useRef(null);
  const releaseTab = useRef(null);
  const audio = useRef(null);
  const mounted = useRef(true);
  const generation = useRef(0);
  const config = useRef(options);
  config.current = options;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current++;
      pending.current?.reject(new Error('Phone connection cancelled'));
      managerRef.current?.disconnect().catch(() => {});
      releaseTab.current?.();
    };
  }, []);

  const connectAndRegister = useCallback(({ username, password }) => {
    if (pending.current) return pending.current.promise;
    const currentGeneration = ++generation.current;
    const isCurrent = () => mounted.current && generation.current === currentGeneration;
    let resolveRegistration, rejectRegistration;
    const promise = new Promise((resolve, reject) => { resolveRegistration = resolve; rejectRegistration = reject; });
    const timer = setTimeout(() => finish(new Error('Phone registration timed out')), 20000);
    const finish = error => {
      if (pending.current?.promise !== promise) return;
      clearTimeout(timer);
      pending.current = null;
      if (error) {
        generation.current++;
        managerRef.current?.disconnect().catch(() => {});
        releaseTab.current?.();
        rejectRegistration(error);
      } else resolveRegistration();
    };
    pending.current = { promise, reject: finish };
    const updateSession = (session, field) => {
      if (!isCurrent()) return;
      setSessions(previous => ({ ...previous, [session.id]: session }));
      if (field) setSessionTimer(previous => ({ ...previous, [session.id]: { ...previous[session.id], [field]: new Date() } }));
    };
    (async () => {
      await managerRef.current?.disconnect();
      releaseTab.current?.();
      if (navigator.locks) {
        await new Promise((resolve, reject) => {
          navigator.locks.request(`pbx-phone:${username}`, { ifAvailable: true }, async lock => {
            if (!lock) { reject(new Error('This phone is open in another tab. Close that tab, then reload.')); return; }
            await new Promise(release => { releaseTab.current = release; resolve(); });
          }).catch(reject);
        });
      }
      if (!isCurrent()) { releaseTab.current?.(); throw new Error('Phone connection cancelled'); }
      const response = await CallGETAPI('api/phone/ice');
      if (!isCurrent() || pending.current?.promise !== promise) throw new Error('Phone connection cancelled');
      const iceServers = response?.data?.iceServers || [{ urls: 'stun:stun.l.google.com:19302' }];
      const manager = new Web.SessionManager(config.current.webSocketServer, {
        aor: `sip:${username}@${config.current.domain}`,
        media: { constraints: { audio: true, video: false }, remote: { audio: audio.current } },
        reconnectionAttempts: 3, reconnectionDelay: 4, registrationRetry: false,
        userAgentOptions: { authorizationUsername: username, authorizationPassword: password,
          logLevel: 'error', logConfiguration: false,
          sessionDescriptionHandlerFactoryOptions: { peerConnectionConfiguration: { iceServers } } },
        registererRegisterOptions: { requestDelegate: { onReject: () => finish(new Error('Phone registration rejected. Check your extension credentials.')) } },
        delegate: {
          onServerConnect: () => {
            if (!isCurrent()) return;
            setConnectStatus(CONNECT_STATUS.CONNECTED);
            if (!manager.shouldBeRegistered) manager.register().catch(finish);
          },
          onServerDisconnect: () => {
            if (!isCurrent()) return;
            setConnectStatus(CONNECT_STATUS.DISCONNECTED);
            setRegisterStatus(REGISTER_STATUS.UNREGISTERED);
          },
          onRegistered: () => { if (isCurrent()) { setRegisterStatus(REGISTER_STATUS.REGISTERED); finish(); } },
          onUnregistered: () => { if (isCurrent()) setRegisterStatus(REGISTER_STATUS.UNREGISTERED); },
          onCallCreated: session => {
            updateSession(session, 'createdAt');
            session.stateChange.addListener(() => updateSession(session));
          },
          onCallReceived: session => updateSession(session, 'receivedAt'),
          onCallAnswered: session => { updateSession(session, 'answeredAt'); collectCallDiagnostics(session); },
          onCallHangup: session => updateSession(session, 'hangupAt'),
        },
      });
      managerRef.current = manager;
      setSessionManager(manager);
      await manager.connect();
    })().catch(finish);
    return promise;
  }, []);

  return <Context.Provider value={{ sessionManager, sessions, sessionTimer, connectAndRegister, connectStatus, registerStatus }}>
    <audio ref={audio} autoPlay />{children}
  </Context.Provider>;
}

export function useSessionCall(id) {
  const { sessions, sessionTimer, sessionManager } = useSIPProvider();
  const session = sessions[id];
  const [isMuted, setMuted] = useState(false);
  const [isHeld, setHeld] = useState(false);
  return { session, timer: sessionTimer[id], direction: session instanceof Inviter ? SessionDirection.OUTGOING : SessionDirection.INCOMING,
    isMuted, isHeld,
    answer: () => sessionManager?.answer(session), decline: () => sessionManager?.decline(session),
    hangup: () => sessionManager?.hangup(session),
    mute: () => { sessionManager?.mute(session); setMuted(true); },
    unmute: () => { sessionManager?.unmute(session); setMuted(false); },
    hold: async () => { await sessionManager?.hold(session); setHeld(true); },
    unhold: async () => { await sessionManager?.unhold(session); setHeld(false); },
  };
}
